// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setUserFields from "@insite/client-framework/Store/Pages/UserSetup/Handlers/SetUserFields";
import { getCurrentEditingUser } from "@insite/client-framework/Store/Pages/UserSetup/UserSetupSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Link from "@insite/mobius/Link";
import { LinkPresentationProps } from '@insite/mobius/Link';
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Tooltip, { TooltipPresentationProps } from "@rc/MobiusOverrides/Tooltip/Tooltip";
import { UsernameCriteria } from "@rc/Overrides/Widgets/UserList/CreateUserModal";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";


const mapStateToProps = (state: ApplicationState) => ({
    editingUser: getCurrentEditingUser(state),
    emailErrorMessage: state.pages.userSetup.emailErrorMessage,
    firstNameErrorMessage: state.pages.userSetup.firstNameErrorMessage,
    lastNameErrorMessage: state.pages.userSetup.lastNameErrorMessage,
    useEmailAsUserName: getSettingsCollection(state).accountSettings.useEmailAsUserName,
});

const mapDispatchToProps = {
    setUserFields,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface UserSetupUserInformationStyles {
    title?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    userNameTextField?: TextFieldPresentationProps;
    emailAddressTextField?: TextFieldPresentationProps;
    firstNameTextField?: TextFieldPresentationProps;
    lastNameTextField?: TextFieldPresentationProps;
    // XNG-Changes
    userNameCriteriaTooltip?: TooltipPresentationProps;
    userNameCriteriaLink?: LinkPresentationProps;
}

export const userSetupUserInformationStyles: UserSetupUserInformationStyles = {
    title: { variant: "h5" },
    wrapper: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    userNameCriteriaLink: {
        typographyProps: {
            size: 12,
            css: css`
                margin-top: 5px;
            `,
        },
        color: "primary",
    },
    userNameCriteriaTooltip: {
        iconProps: {
            css: css`
                margin-left: 5px;
            `,
        },
        cssOverrides: {
            tooltipClickable: css`
                display: flex;
            `,
        },
    },
};

const styles = userSetupUserInformationStyles;

const formId = "userSetupForm";

const UserSetupUserInformation = ({
    editingUser,
    emailErrorMessage,
    firstNameErrorMessage,
    lastNameErrorMessage,
    useEmailAsUserName,
    setUserFields,
}: Props) => {
    if (!editingUser) {
        return null;
    }

    const [email, setEmail] = useState(editingUser.email);
    const [userName, setUserName] = useState(editingUser.userName);

    useEffect(() => {
        setEmail(editingUser.email);
    }, [editingUser.email]);

    useEffect(() => {
        setUserName(editingUser.userName);
    }, [editingUser.userName]);

    const emailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUserFields({ email: event.target.value });
        setEmail(event.target.value);
        if (useEmailAsUserName) {
            setUserName(event.target.value);
        }
    };

    const emailBlurHandler = () => {
        setUserFields({ email });
    };

    const firstNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUserFields({ firstName: event.target.value });
    };

    const lastNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUserFields({ lastName: event.target.value });
    };

    return (
        <>
            <Typography as="h2" {...styles.title} data-test-selector="userSetupInfo_title">
                {translate("User Information")}
            </Typography>
            <StyledWrapper {...styles.wrapper}>
                <TextField
                    value={userName}
                    label={translate("Username")}
                    {...styles.userNameTextField}
                    disabled={true}
                    aria-labelledby="userName"
                    data-test-selector="userSetupInfo_userName"
                />
                {/* XNG-Changes */}
                <Tooltip
                    trigger="click"
                    {...styles.userNameCriteriaTooltip}
                    triggerComponent={
                        <Link
                            onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
                                event.preventDefault();
                            }}
                            {...styles.userNameCriteriaLink}
                        >
                            {translate(" Click to learn more about Username Criteria")}
                        </Link>
                    }
                    passAsChildren={true}
                >
                    <UsernameCriteria />
                </Tooltip>
            </StyledWrapper>
            <StyledWrapper {...styles.wrapper}>
                <TextField
                    value={email}
                    label={translate("Email Address")}
                    {...styles.emailAddressTextField}
                    aria-labelledby="emailAddress"
                    maxLength={256}
                    required
                    onChange={emailChangeHandler}
                    onBlur={emailBlurHandler}
                    error={emailErrorMessage}
                    data-test-selector="userSetupInfo_email"
                    type="email"
                    {...{ formId }}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.wrapper}>
                <TextField
                    value={editingUser.firstName}
                    label={translate("First Name")}
                    {...styles.firstNameTextField}
                    aria-labelledby="firstName"
                    maxLength={100}
                    required
                    onChange={firstNameChangeHandler}
                    error={firstNameErrorMessage}
                    data-test-selector="userSetupInfo_firstName"
                    type="firstName"
                    {...{ formId }}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.wrapper}>
                <TextField
                    value={editingUser.lastName}
                    label={translate("Last Name")}
                    {...styles.lastNameTextField}
                    aria-labelledby="lastName"
                    maxLength={100}
                    required
                    onChange={lastNameChangeHandler}
                    error={lastNameErrorMessage}
                    data-test-selector="userSetupInfo_lastName"
                    type="lastName"
                    {...{ formId }}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.wrapper}>
                {siteMessage("Custom_UserAdmin_CreateUserModalTermsAndConditions")}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(UserSetupUserInformation),
    definition: {
        group: "User Setup",
        displayName: "User Information",
        allowedContexts: ["UserSetupPage"],
    },
};

export default widgetModule;
